import { EasingFunction } from '../types';

export class AnimationEngine {
  private rafId: number | null = null;
  private isRunning: boolean = false;
  private lastTimestamp: number = 0;
  private currentTimeMs: number = 0;
  private speed: number = 1.0;

  private onTickCallback: ((timeMs: number, deltaMs: number) => void) | null = null;

  public static Easings = {
    linear: (t: number) => t,
    easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    easeOutCubic: (t: number) => --t * t * t + 1,
    easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
    easeOutBack: (t: number) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    },
    easeInOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  };

  constructor(speed: number = 1.0) {
    this.speed = speed;
  }

  public setSpeed(speed: number): void {
    this.speed = Math.max(0.1, Math.min(10.0, speed));
  }

  public getSpeed(): number {
    return this.speed;
  }

  public onTick(callback: (timeMs: number, deltaMs: number) => void): void {
    this.onTickCallback = callback;
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTimestamp = performance.now();
    this.loop(this.lastTimestamp);
  }

  public pause(): void {
    this.isRunning = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  public stop(): void {
    this.pause();
    this.currentTimeMs = 0;
  }

  public setCurrentTime(timeMs: number): void {
    this.currentTimeMs = Math.max(0, timeMs);
    if (this.onTickCallback) {
      this.onTickCallback(this.currentTimeMs, 0);
    }
  }

  /**
   * Set the current time WITHOUT firing the tick callback.
   * Use this to reposition without triggering Timeline's handleTick.
   */
  public setSilentTime(timeMs: number): void {
    this.currentTimeMs = Math.max(0, timeMs);
  }

  public getCurrentTime(): number {
    return this.currentTimeMs;
  }

  public isActive(): boolean {
    return this.isRunning;
  }

  private loop = (timestamp: number): void => {
    if (!this.isRunning) return;

    const rawDelta = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    // Apply speed scaling to delta time
    const scaledDelta = rawDelta * this.speed;
    this.currentTimeMs += scaledDelta;

    if (this.onTickCallback) {
      this.onTickCallback(this.currentTimeMs, scaledDelta);
    }

    this.rafId = requestAnimationFrame(this.loop);
  };
}
